import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "./Events.css";
import { fetchEventById, getGalleryUrlsForEvent } from "../services/eventsApi";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

function initials(name) {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

function EventImageLightbox({ urls, index, onClose, onPrev, onNext }) {
  const safeIndex = Math.min(Math.max(0, index), urls.length - 1);
  const url = urls[safeIndex];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext]);

  if (!url) return null;

  return (
    <div
      className="eventLightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Full screen image"
      onClick={onClose}
    >
      <button type="button" className="eventLightboxClose" onClick={onClose} aria-label="Close">
        ×
      </button>
      {urls.length > 1 && (
        <button
          type="button"
          className="eventLightboxNav eventLightboxPrev"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          aria-label="Previous image"
        >
          ‹
        </button>
      )}
      <div className="eventLightboxInner" onClick={(e) => e.stopPropagation()}>
        <img className="eventLightboxImg" src={url} alt="" />
        {urls.length > 1 && (
          <div className="eventLightboxCounter" aria-live="polite">
            {safeIndex + 1} / {urls.length}
          </div>
        )}
      </div>
      {urls.length > 1 && (
        <button
          type="button"
          className="eventLightboxNav eventLightboxNext"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          aria-label="Next image"
        >
          ›
        </button>
      )}
    </div>
  );
}

export default function EventDetail() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const data = await fetchEventById(eventId);
        if (cancelled) return;
        if (!data) {
          setEvent(null);
          setError("notfound");
        } else {
          setEvent(data);
        }
      } catch (e) {
        if (cancelled) return;
        setError("load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  useEffect(() => {
    setLightboxIndex(null);
  }, [eventId]);

  const bodyText = event ? event.content || event.excerpt || "" : "";
  const paragraphs = bodyText.split(/\n\n+/).filter(Boolean);
  const galleryUrls = event ? getGalleryUrlsForEvent(event) : [];
  const hasGallery = galleryUrls.length > 0;

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const goPrev = useCallback(() => {
    setLightboxIndex((i) => {
      if (i == null || !galleryUrls.length) return i;
      return (i - 1 + galleryUrls.length) % galleryUrls.length;
    });
  }, [galleryUrls.length]);
  const goNext = useCallback(() => {
    setLightboxIndex((i) => {
      if (i == null || !galleryUrls.length) return i;
      return (i + 1) % galleryUrls.length;
    });
  }, [galleryUrls.length]);

  return (
    <div className="eventsPage eventDetailPage">
      <div className={`eventsWrap eventDetailWrap ${hasGallery ? "eventDetailWrapWide" : ""}`}>
        <Link className="eventDetailBack" to="/Events">
          ← Back to events
        </Link>

        {loading ? (
          <div className="eventDetailLoading" aria-hidden="true">
            <div className="eventsSkelImg eventDetailSkelHero" />
            <div className="eventsSkelLine w40" />
            <div className="eventsSkelLine w95" />
            <div className="eventsSkelLine w90" />
            <div className="eventsSkelLine w88" />
          </div>
        ) : error === "notfound" ? (
          <div className="eventsError">
            <div className="eventsErrorTitle">Event not found</div>
            <div className="eventsErrorMsg">This event may have been removed or the link is incorrect.</div>
            <Link className="eventsBtn eventDetailBackBtn" to="/Events">
              All events
            </Link>
          </div>
        ) : error === "load" ? (
          <div className="eventsError">
            <div className="eventsErrorTitle">Something went wrong</div>
            <div className="eventsErrorMsg">Failed to load this event.</div>
            <button className="eventsBtn" type="button" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        ) : event ? (
          <article className="eventDetailArticle">
            <div className="eventDetailHero">
              <img src={event.imageUrl} alt="" />
            </div>
            <div className="eventDetailMeta eventsMeta">
              <span className="eventsMetaStrong">
                {event.category}, {event.tag}
              </span>
              <span className="eventsDot">—</span>
              <span>{formatDate(event.date)}</span>
            </div>
            <h1 className="eventDetailTitle">{event.title}</h1>

            <div className={hasGallery ? "eventDetailMain eventDetailMainWithGallery" : "eventDetailMain"}>
              <div className="eventDetailBody">
                {paragraphs.map((p, i) => (
                  <p key={i} className="eventDetailPara">
                    {p}
                  </p>
                ))}
              </div>
              {hasGallery ? (
                <aside className="eventDetailGalleryAside" aria-label="Event photo gallery">
                  <h2 className="eventDetailGalleryHeading">Gallery</h2>
                  <div className="eventDetailGalleryGrid">
                    {galleryUrls.map((url, i) => (
                      <button
                        key={`${url}-${i}`}
                        type="button"
                        className="eventDetailGalleryThumb"
                        onClick={() => setLightboxIndex(i)}
                      >
                        <img src={url} alt="" loading="lazy" />
                      </button>
                    ))}
                  </div>
                </aside>
              ) : null}
            </div>

            <div className="eventsAuthor eventDetailAuthor">
              <div className="eventsAvatar" aria-hidden="true">
                {initials(event.author?.name)}
              </div>
              <div>
                <div className="eventsAuthorName">{event.author?.name}</div>
                <div className="eventsAuthorRole">{event.author?.role}</div>
              </div>
            </div>
          </article>
        ) : null}
      </div>

      {lightboxIndex != null && galleryUrls.length > 0 ? (
        <EventImageLightbox
          urls={galleryUrls}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={goPrev}
          onNext={goNext}
        />
      ) : null}
    </div>
  );
}
